import noSelectedText from "Common/componentUtils/noSelectedText";
import useIsMountedRef from "Common/componentUtils/useIsMountedRef";
import usePrevious from "Common/componentUtils/usePrevious";
import { SORT_ORDER } from "Common/dictionaries/sortOrder";
import withOverlaysProvider from "Components/overlaysContext/withOverlaysProvider";
import { HeightDetector } from "Components/responsive";
import withPopupMessage from "Components/withPopupMessage";
import classNames from "classnames";
import { Column, FilterPanel, FilterRow, HeaderFilter, Paging, Scrolling, SearchPanel, Selection, Sorting } from "devextreme-react/data-grid";
import forEach from "lodash/forEach";
import get from "lodash/get";
import isArray from "lodash/isArray";
import isEqual from "lodash/isEqual";
import isNil from "lodash/isNil";
import isNumber from "lodash/isNumber";
import some from "lodash/some";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MAKE_TABLE_VISIBLE_TIMEOUT, UPDATE_DIMENSIONS_DEFAULT_TIMEOUT } from "./constants";
import locale from "./dataGridBase.locale";
import Styled from "./dataGridBase.styled";
import FilterBuilderPopup, { filterBuilderFieldDefaultOptions } from "./filterBuilderPopup";
import LoadPanel from "./loadPanel";
import { CELL_NAME_FOR_STORE_KEY_FIELD, HIGHLIGHT_FIELD_NAME, buildDataSource, buildRemoteDataSource, clearSelection, updateDimensions } from "./utils";
const visibleColumnRegexp = /columns\[\d+\]\.visible/;
const widthColumnRegexp = /columns\[\d+\]\.width/;
const headerFilterSearch = { enabled: true };
function DataGridBase(props) {
    const isMountedRef = useIsMountedRef();
    const [availableHeight, setAvailableHeight] = useState(0);
    const prevAvailableHeight = usePrevious(availableHeight);
    const defaultGridRef = useRef(null);
    const ref = props.gridRef || defaultGridRef;
    const gridWrapper = useRef(null);
    const columnAutoWidthWasApplied = useRef(false);
    const prevData = useRef(props.data);
    const [innerDataSource, setInnerDataSource] = useState(() => props.remoteOperations ? undefined : buildDataSource(props.data, undefined, null, props.keyExpr));
    const dataSourceWasApplied = useRef(false);
    const timerRef = useRef(null);
    useEffect(() => () => {
        timerRef.current && clearTimeout(timerRef.current);
    }, []);
    useEffect(() => {
        var _a, _b, _c;
        if (!props.loadUrl && ((_a = ref.current) === null || _a === void 0 ? void 0 : _a.instance) && isEqual(prevData.current, props.data)) {
            prevData.current = props.data;
            dataSourceWasApplied.current = true;
            return;
        }
        if (!props.data && !props.loadUrl) {
            prevData.current = props.data;
            setInnerDataSource(undefined);
            dataSourceWasApplied.current = true;
            return;
        }
        dataSourceWasApplied.current = false;
        const createdNewDataSource = props.remoteOperations
            ? buildRemoteDataSource(props.loadUrl || "", props.onInProgressChanged, props.onHasDataChanged, props.showError)
            : buildDataSource(props.data, prevData.current, ref.current, props.keyExpr);
        prevData.current = props.data;
        if (createdNewDataSource) {
            setInnerDataSource(createdNewDataSource);
        }
        else if (isMountedRef.current &&
            props.clearSelectionAfterUpdate &&
            ((_c = (_b = ref.current) === null || _b === void 0 ? void 0 : _b.instance) === null || _c === void 0 ? void 0 : _c.clearSelection)) {
            clearSelection(ref.current);
            ref.current.instance.refresh();
        }
        dataSourceWasApplied.current = true;
    }, [props.data, props.loadUrl]);
    const handleContentReady = useCallback((e) => {
        var _a;
        if (!e.component)
            return;
        if (e.component && !columnAutoWidthWasApplied.current) {
            forEach(e.component.getVisibleColumns(), (column) => {
                if (!e.component)
                    return;
                const item = column;
                if (item.visibleWidth && (!column.width || column.width === "auto")) {
                    e.component.columnOption(item.index, "width", item.visibleWidth);
                }
            });
            columnAutoWidthWasApplied.current = true;
            updateDimensions(ref.current, MAKE_TABLE_VISIBLE_TIMEOUT, () => {
                var _a, _b;
                (_b = (_a = ref.current) === null || _a === void 0 ? void 0 : _a.instance.element()) === null || _b === void 0 ? void 0 : _b.classList.remove("invisible");
            });
        }
        const scrollView = (_a = e.component.getScrollable()) === null || _a === void 0 ? void 0 : _a.instance();
        scrollView &&
            !scrollView.option("onStart") &&
            props.hidePopoverOverlays &&
            scrollView.option("onStart", props.hidePopoverOverlays);
        props.onContentReady && props.onContentReady(e);
    }, [props.onContentReady, props.hidePopoverOverlays]);
    const handleHeightChanged = useCallback((h) => {
        if (Math.floor(h) !== availableHeight && isMountedRef.current) {
            setAvailableHeight(Math.floor(h));
        }
    }, []);
    const handleOptionChange = useCallback((e) => {
        var _a;
        props.onOptionChanged && props.onOptionChanged(e);
        const component = (_a = ref.current) === null || _a === void 0 ? void 0 : _a.instance;
        if (!component)
            return;
        if (e.fullName === "onInitialized" ||
            (e.fullName && visibleColumnRegexp.test(e.fullName) && e.value)) {
            columnAutoWidthWasApplied.current = false;
        }
        else if (e.fullName &&
            widthColumnRegexp.test(e.fullName) &&
            columnAutoWidthWasApplied.current) {
            updateDimensions(ref.current, UPDATE_DIMENSIONS_DEFAULT_TIMEOUT);
        }
        else if (props.onSetSelected && e.fullName === "selectionFilter") {
            if (!dataSourceWasApplied.current) {
                timerRef.current && clearTimeout(timerRef.current);
                timerRef.current = setTimeout(() => {
                    if (isMountedRef.current && (component === null || component === void 0 ? void 0 : component.clearSelection))
                        component.clearSelection();
                }, 0);
                return;
            }
            timerRef.current && clearTimeout(timerRef.current);
            timerRef.current = setTimeout(() => {
                var _a, _b;
                if (!((_a = ref.current) === null || _a === void 0 ? void 0 : _a.instance.element().isConnected))
                    return;
                const store = (_b = ref.current.instance.getDataSource()) === null || _b === void 0 ? void 0 : _b.store();
                if (!store)
                    return;
                const allCheckBox = ref.current.instance
                    .element()
                    .querySelector(".dx-header-row .dx-command-select .dx-checkbox");
                if (!allCheckBox)
                    return;
                const allChecked = allCheckBox.classList.contains("dx-checkbox-checked");
                const allIndeterminate = allCheckBox.classList.contains("dx-checkbox-indeterminate");
                if (!allChecked && !allIndeterminate) {
                    props.onSetSelected && props.onSetSelected([]);
                }
                else {
                    store.load({ filter: e.value }).then((newSelected) => {
                        props.onSetSelected && props.onSetSelected(newSelected);
                    });
                }
            }, 0);
        }
    }, [props.onOptionChanged, props.onSetSelected]);
    const handleCellPrepare = useCallback((e) => {
        if (props.highlightUpdates) {
            if (e.rowType === "data" && e.data[HIGHLIGHT_FIELD_NAME.HIGHLIGHT] && get(e, "row.cells")) {
                e.cellElement.classList.add("highlighted-cell");
                e.data[HIGHLIGHT_FIELD_NAME.HIGHLIGHTED_CELLS_COUNT] =
                    (e.data[HIGHLIGHT_FIELD_NAME.HIGHLIGHTED_CELLS_COUNT] || 0) + 1;
                if (isNumber(get(e, "values.length")) &&
                    e.data[HIGHLIGHT_FIELD_NAME.HIGHLIGHTED_CELLS_COUNT] >= get(e, "values.length", 0)) {
                    e.data[HIGHLIGHT_FIELD_NAME.HIGHLIGHTED_TIMER_ID] &&
                        clearTimeout(e.data[HIGHLIGHT_FIELD_NAME.HIGHLIGHTED_TIMER_ID]);
                    e.data[HIGHLIGHT_FIELD_NAME.HIGHLIGHTED_TIMER_ID] = setTimeout(() => {
                        e.data[HIGHLIGHT_FIELD_NAME.HIGHLIGHT] = undefined;
                        e.data[HIGHLIGHT_FIELD_NAME.HIGHLIGHTED_CELLS_COUNT] = undefined;
                    }, 10);
                }
            }
        }
        props.onCellPrepared && props.onCellPrepared(e);
    }, [props.highlightUpdates, props.onCellPrepared]);
    const handleRowClick = useCallback((e) => {
        if (!noSelectedText())
            return;
        if (get(e.event, "originalEvent.originalEvent.target.classList.contains") &&
            e.event.originalEvent.originalEvent.target.classList.contains("dx-prevent-propagation-click"))
            return;
        props.onRowClick && props.onRowClick(e);
    }, [props.onRowClick]);
    const handleCustomizeColumns = useCallback((columns) => {
        const hasCellTemplates = some(columns, "cellTemplate");
        columns.forEach(column => {
            if (column.dataType === "date" && !column.width)
                column.width = 155;
            if (column.name !== CELL_NAME_FOR_STORE_KEY_FIELD) {
                if (column.dataField && !column.userDataType) {
                    throw new Error(`Please, set dataType for column ${column.dataField}`);
                }
                if (props.cellTemplateRequired &&
                    hasCellTemplates &&
                    column.visible !== false &&
                    !column.cellTemplate) {
                    throw new Error(`Please, set cellRender method for column ${column.dataField}`);
                }
            }
        });
    }, [props.cellTemplateRequired]);
    const handleRowPrepared = useCallback((e) => {
        if (!props.rowClassName || e.rowType !== "props.data")
            return;
        e.rowElement.classList.add(props.rowClassName(e.data));
    }, [props.rowClassName]);
    const handleFilterValueChange = useCallback((value) => {
        setTimeout(() => {
            var _a, _b;
            if (!props.onSetSelected || !((_a = ref.current) === null || _a === void 0 ? void 0 : _a.instance.element().isConnected))
                return;
            (_b = ref.current) === null || _b === void 0 ? void 0 : _b.instance.deselectAll();
            props.onSetSelected([]);
        }, 0);
        props.onFilterValueChange && props.onFilterValueChange(value);
    }, [props.onFilterValueChange, props.onSetSelected]);
    useEffect(() => {
        prevAvailableHeight && updateDimensions(ref.current, UPDATE_DIMENSIONS_DEFAULT_TIMEOUT);
    }, [availableHeight]);
    const readyToShowDataGrid = Boolean((props.wrapperHeight || availableHeight) && innerDataSource);
    useEffect(() => {
        if (readyToShowDataGrid && ref.current) {
            ref.current.instance.option("elementAttr", props.elementAttr);
            columnAutoWidthWasApplied.current &&
                ref.current.instance.element().classList.remove("invisible");
        }
    }, [props.elementAttr, readyToShowDataGrid]);
    const style = useMemo(() => (isNil(props.wrapperHeight) ? {} : { height: props.wrapperHeight }), [props.wrapperHeight]);
    const filterBuilder = useMemo(() => props.allFilterPanelsOn && props.filterBuilderFields
        ? {
            fields: props.filterBuilderFields.map(item => ({
                ...filterBuilderFieldDefaultOptions,
                ...item
            }))
        }
        : undefined, [props.filterBuilderFields, props.allFilterPanelsOn]);
    return (React.createElement("div", { ref: gridWrapper, style: style, className: classNames(props.className, "stretched-to-fit overflow-hidden") },
        React.createElement("div", { ref: gridWrapper, className: "stretched-to-fit overflow-hidden" },
            isNil(props.wrapperHeight) && (React.createElement(HeightDetector, { minBottomOffset: props.minBottomOffset, onChangeAvailableHeight: handleHeightChanged, wrapperRef: gridWrapper, useFixedResizableElement: props.useFixedResizableElement })),
            useMemo(() => readyToShowDataGrid ? (React.createElement(Styled.DataGrid, { className: classNames("overflow-hidden", !columnAutoWidthWasApplied.current && "invisible", props.gridClassName), minRowHeight: props.minRowHeight, ref: ref, showBorders: props.showBorders, showRowLines: props.showRowLines, showColumnLines: props.showColumnLines, allowColumnResizing: props.allowColumnResizing, columnResizingMode: props.columnResizingMode, columnAutoWidth: !columnAutoWidthWasApplied.current, columnMinWidth: props.columnMinWidth, showColumnHeaders: props.showColumnHeaders, rowAlternationEnabled: false, noDataText: props.noDataText, repaintChangesOnly: true, cellHintEnabled: false, autoNavigateToFocusedRow: false, focusedRowEnabled: false, renderAsync: false, cacheEnabled: props.cacheEnabled, wordWrapEnabled: true, dataSource: innerDataSource, height: props.wrapperHeight || availableHeight, syncLookupFilterValues: props.syncLookupFilterValues, remoteOperations: props.remoteOperations, width: props.gridWidth, onContentReady: handleContentReady, onEditCanceled: props.onEditCanceled, onOptionChanged: handleOptionChange, onCellPrepared: handleCellPrepare, onRowClick: handleRowClick, onInitialized: props.onInitialized, hoverStateEnabled: Boolean(props.onRowClick), onRowPrepared: handleRowPrepared, customizeColumns: handleCustomizeColumns, onRowInserting: props.onRowInserting, onRowUpdating: props.onRowUpdating, onRowUpdated: props.onRowUpdated, onKeyDown: props.onKeyDown, onFilterValueChange: handleFilterValueChange, filterBuilder: filterBuilder },
                props.remoteOperations ? LoadPanel : null,
                React.createElement(Sorting, { mode: "single" }),
                props.searchPanelOn ? React.createElement(SearchPanel, { visible: true, placeholder: locale.SEARCH }) : null,
                React.createElement(Scrolling, { mode: "virtual", rowRenderingMode: "standard", renderAsync: false, preloadEnabled: true }),
                React.createElement(Paging, { enabled: false }),
                Boolean(props.onSetSelected) && (React.createElement(Selection, { mode: "multiple", showCheckBoxesMode: "always", selectAllMode: "allPages", deferred: true })),
                props.allFilterPanelsOn && React.createElement(FilterRow, { visible: true }),
                props.allFilterPanelsOn && React.createElement(FilterPanel, { visible: true }),
                props.allFilterPanelsOn && React.createElement(FilterBuilderPopup, null),
                props.allFilterPanelsOn && (React.createElement(HeaderFilter, { visible: true, search: headerFilterSearch, width: 300 })),
                get(innerDataSource, "store._key") &&
                    isArray(props.children) &&
                    !some(props.children, "props.sortOrder") &&
                    !some(props.children, ["props.dataField", get(innerDataSource, "store._key")]) ? (React.createElement(Column, { dataField: get(innerDataSource, "store._key"), sortOrder: SORT_ORDER.DESC, width: 0, visible: false, defaultVisible: false, name: CELL_NAME_FOR_STORE_KEY_FIELD, allowHiding: false, showInColumnChooser: false, allowSearch: false })) : undefined,
                props.children)) : null, [
                readyToShowDataGrid,
                innerDataSource,
                props.wrapperHeight || availableHeight,
                props.children,
                props.elementAttr,
                props.showBorders,
                props.gridClassName,
                props.noDataText,
                props.showColumnHeaders,
                props.minRowHeight,
                props.columnMinWidth,
                handleContentReady,
                props.onRowInserting,
                props.onRowUpdating,
                props.onRowUpdated,
                props.onKeyDown,
                handleRowClick,
                handleOptionChange,
                handleCellPrepare,
                props.onEditCanceled,
                props.onInitialized,
                handleRowPrepared,
                handleCustomizeColumns,
                handleFilterValueChange,
                props.cacheEnabled
            ]))));
}
export default function DataGridBaseWithProviders(props) {
    const Hoc = useMemo(() => props.withPopupMessage
        ? withPopupMessage(withOverlaysProvider(DataGridBase))
        : withOverlaysProvider(DataGridBase), [props.withPopupMessage]);
    return React.createElement(Hoc, { ...props });
}
DataGridBaseWithProviders.defaultProps = {
    allFilterPanelsOn: true,
    searchPanelOn: false,
    minBottomOffset: 20,
    minRowHeight: 50,
    syncLookupFilterValues: false,
    remoteOperations: false,
    useFixedResizableElement: true,
    showBorders: true,
    allowColumnResizing: true,
    columnResizingMode: "nextColumn",
    highlightUpdates: false,
    cellTemplateRequired: true,
    clearSelectionAfterUpdate: true,
    withPopupMessage: true,
    gridWidth: "100%",
    showRowLines: true,
    showColumnLines: true,
    showColumnHeaders: true,
    noDataText: locale.NO_DATA_TO_DISPLAY,
    columnMinWidth: 25,
    cacheEnabled: true
};
